import { AfterViewInit, Component, ElementRef, inject, OnDestroy } from '@angular/core';
import * as THREE from 'three';
import { SPHERE_FRAG, SPHERE_VERT } from './constants';

@Component({
  selector: 'sphere-animation',
  template: ` <canvas class="home__sphere" aria-hidden="true"></canvas> `,
  styles: `
    .home__sphere {
      position: fixed;
      right: -8%;
      top: 5%;
      transform: translate(0, 0) scale(1.2);
      filter: blur(0) brightness(1);
      pointer-events: none;
      z-index: 0;
      opacity: 0.22;

      @media (max-width: 768px) {
        display: none;
      }
    }
  `,
})
export class SphereAnimation implements AfterViewInit, OnDestroy {
  readonly el = inject(ElementRef<HTMLElement>);
  private sphereRaf?: number;
  private sphereRenderer?: THREE.WebGLRenderer;
  private sphereCleanupFns: Array<() => void> = [];

  ngAfterViewInit() {
    this.initParticleSphere();
  }

  ngOnDestroy() {
    if (this.sphereRaf !== undefined) cancelAnimationFrame(this.sphereRaf);
    this.sphereRenderer?.dispose();
    this.sphereCleanupFns.forEach((fn) => fn());
  }

  private initParticleSphere(): void {
    const host = this.el.nativeElement;
    const canvas = host.querySelector('.home__sphere') as HTMLCanvasElement | null;
    if (!canvas) return;

    const size = 640;
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(size, size, false);
    renderer.setClearColor(0x000000, 0);
    this.sphereRenderer = renderer;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(52, 1, 1, 2000);
    camera.position.z = 540;

    const N_LAT = 105,
      N_LON = 105;
    const count = (N_LAT - 1) * N_LON;
    const phiArr = new Float32Array(count);
    const thetaArr = new Float32Array(count);
    let idx = 0;
    for (let lat = 1; lat < N_LAT; lat++) {
      for (let lon = 0; lon < N_LON; lon++) {
        phiArr[idx] = (lat / N_LAT) * Math.PI;
        thetaArr[idx] = (lon / N_LON) * Math.PI * 2;
        idx++;
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    geo.setAttribute('sPhi', new THREE.BufferAttribute(phiArr, 1));
    geo.setAttribute('sTheta', new THREE.BufferAttribute(thetaArr, 1));

    const dpr = Math.min(window.devicePixelRatio, 2);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uDpr: { value: dpr } },
      vertexShader: SPHERE_VERT,
      fragmentShader: SPHERE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    scene.add(points);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const startTime = performance.now();

    let visible = true;
    const io = new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
      },
      { threshold: 0 },
    );
    io.observe(canvas);
    this.sphereCleanupFns.push(() => io.disconnect());

    const animate = (): void => {
      this.sphereRaf = requestAnimationFrame(animate);
      if (!visible) return;
      if (!reduceMotion) mat.uniforms['uTime'].value = (performance.now() - startTime) / 1000;
      renderer.render(scene, camera);
    };
    animate();
  }
}
